import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Container,
    Button,
    TextField,
    MenuItem,
    Grid,
    LinearProgress,
    TableSortLabel
} from "@mui/material";
import {
    ArrowBack,
    Download,
    EmojiEvents,
    PictureAsPdf
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function LearningPathScoreboardPage() {
    const navigate = useNavigate();
    const { pathId } = useParams();
    const [path, setPath] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [pathFilter, setPathFilter] = useState(pathId || "all");
    const [minScore, setMinScore] = useState("");
    const [sortField, setSortField] = useState("performanceScore");
    const [sortOrder, setSortOrder] = useState("desc");
    const [allPaths, setAllPaths] = useState([]);

    useEffect(() => {
        let unsub = null;
        async function init() {
            try {
                setLoading(true);
                const paths = await learningService.getLearningPaths(false);
                setAllPaths(paths);
                if (pathId) {
                    const pathData = await learningService.getLearningPathById(pathId);
                    setPath(pathData);
                }
                unsub = learningService.listenComprehensiveScoreboard(pathId || null, (list) => {
                    setRows(list);
                    setLoading(false);
                });
            } catch (_) {
                setLoading(false);
            }
        }
        init();
        return () => { if (unsub) unsub(); };
    }, [pathId]);

    useEffect(() => {
        if (pathId) setPathFilter(pathId);
    }, [pathId]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const min = minScore === "" ? null : Number(minScore);
        let list = rows.filter((r) => {
            if (pathFilter !== "all" && r.pathId !== pathFilter) return false;
            if (statusFilter !== "all" && r.completionStatus !== statusFilter) return false;
            if (min !== null && Number(r.performanceScore || 0) < min) return false;
            if (!q) return true;
            return `${r.userName} ${r.userEmail} ${r.pathName}`.toLowerCase().includes(q);
        });
        list = list.slice().sort((a, b) => {
            const av = sortField === "name" ? String(a.userName || "").toLowerCase() : Number(a[sortField] || 0);
            const bv = sortField === "name" ? String(b.userName || "").toLowerCase() : Number(b[sortField] || 0);
            if (av < bv) return sortOrder === "asc" ? -1 : 1;
            if (av > bv) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return list;
    }, [rows, search, statusFilter, pathFilter, minScore, sortField, sortOrder]);

    const stats = useMemo(() => {
        const base = filteredRows;
        return {
            total: base.length,
            started: base.filter((r) => r.started).length,
            completed: base.filter((r) => r.pathCompleted).length,
            highPerformers: base.filter((r) => Number(r.performanceScore || 0) >= 80).length
        };
    }, [filteredRows]);

    const downloadCsv = () => {
        const headers = ["Path", "Student Name", "Email", "Started", "Progress %", "Completion", "Performance Score", "Attempts", "Credits"];
        const lines = [headers.join(",")];
        filteredRows.forEach((r) => {
            lines.push([
                r.pathName,
                r.userName,
                r.userEmail,
                r.started ? "Yes" : "No",
                r.progressPercent,
                r.completionStatus,
                r.performanceScore,
                r.attempts,
                r.totalCreditsEarned
            ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${path?.name || "learning-paths"}-scoreboard.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadPdf = () => {
        const popup = window.open("", "_blank", "width=1100,height=800");
        if (!popup) return;
        const rowsHtml = filteredRows.map((r, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${r.pathName}</td>
            <td>${r.userName}</td>
            <td>${r.userEmail}</td>
            <td>${r.started ? "Yes" : "No"}</td>
            <td>${r.progressPercent}%</td>
            <td>${r.completionStatus}</td>
            <td>${r.performanceScore}</td>
            <td>${r.attempts}</td>
          </tr>
        `).join("");
        popup.document.write(`
          <html><head><title>Learning Scoreboard</title></head><body>
          <h2>Learning Paths Scoreboard</h2>
          <table border="1" cellspacing="0" cellpadding="6">
            <thead><tr><th>#</th><th>Path</th><th>Student</th><th>Email</th><th>Started</th><th>Progress</th><th>Status</th><th>Score</th><th>Attempts</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          </body></html>
        `);
        popup.document.close();
        popup.focus();
        popup.print();
    };

    const setSort = (field) => {
        if (sortField === field) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortField(field);
        setSortOrder("desc");
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Path Scoreboard">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (pathId && !path) {
        return (
            <AdminLayout title="Learning Path Scoreboard">
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">Learning path not found</Alert>
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Learning Path Scoreboard">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/admin/learning-paths")}
                        sx={{ mb: 2 }}
                    >
                        Back to Learning Paths
                    </Button>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {pathId ? `${path?.name || "Learning Path"} - Scoreboard` : "All Learning Paths - Scoreboard"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Student progress and performance tracking
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={downloadCsv}
                            sx={{ mr: 1 }}
                        >
                            Download CSV
                        </Button>
                        <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={downloadPdf}>
                            Export PDF
                        </Button>
                    </Box>
                </Box>

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField fullWidth size="small" label="Search Student" value={search} onChange={(e) => setSearch(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    select
                                    label="Path"
                                    value={pathFilter}
                                    onChange={(e) => setPathFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Paths</MenuItem>
                                    {allPaths.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    select
                                    label="Status"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="not_started">Not Started</MenuItem>
                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Min Performance Score"
                                    type="number"
                                    value={minScore}
                                    onChange={(e) => setMinScore(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Total Enrolled
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {stats.total}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Completed
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                                    {stats.completed}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    In Progress
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
                                    {stats.started - stats.completed}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Not Started
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "error.main" }}>
                                    {stats.total - stats.started}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Scoreboard Table */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Ranking
                            </Typography>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                    <TableRow>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Rank
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            <TableSortLabel active={sortField === "name"} direction={sortOrder} onClick={() => setSort("name")}>
                                                Student Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Path</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <TableSortLabel active={sortField === "progressPercent"} direction={sortOrder} onClick={() => setSort("progressPercent")}>
                                                Progress
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <TableSortLabel active={sortField === "performanceScore"} direction={sortOrder} onClick={() => setSort("performanceScore")}>
                                                Performance
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <TableSortLabel active={sortField === "attempts"} direction={sortOrder} onClick={() => setSort("attempts")}>
                                                Attempts
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.map((progress, index) => (
                                        <TableRow
                                            key={progress.id}
                                            hover
                                            sx={{
                                                backgroundColor:
                                                    index === 0
                                                        ? "warning.lighter"
                                                        : index === 1
                                                            ? "info.lighter"
                                                            : index === 2
                                                                ? "success.lighter"
                                                                : "inherit"
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                    {index < 3 ? (
                                                        <EmojiEvents
                                                            sx={{
                                                                color:
                                                                    index === 0
                                                                        ? "#FFD700"
                                                                        : index === 1
                                                                            ? "#C0C0C0"
                                                                            : "#CD7F32"
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontWeight: 700 }}>
                                                            {index + 1}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{progress.userName || "Unknown"}</TableCell>
                                            <TableCell>{progress.pathName}</TableCell>
                                            <TableCell>{progress.userEmail || "N/A"}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={progress.progressPercent}
                                                        sx={{ flex: 1, height: 6 }}
                                                    />
                                                    <Typography variant="caption" sx={{ minWidth: 40, fontWeight: 700 }}>
                                                        {Math.round(progress.progressPercent)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${progress.performanceScore} pts`}
                                                    color={progress.performanceScore >= 80 ? "success" : progress.performanceScore >= 60 ? "warning" : "default"}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">{progress.attempts}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={progress.completionStatus === "completed" ? "Completed" : progress.completionStatus === "in_progress" ? "In Progress" : "Not Started"}
                                                    color={
                                                        progress.completionStatus === "completed"
                                                            ? "success"
                                                            : progress.completionStatus === "in_progress"
                                                                ? "warning"
                                                                : "default"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>
        </AdminLayout>
    );
}
